import {Component, forwardRef, Input} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, ValidationErrors, Validator} from '@angular/forms';

/** Calendar periods are converted to inclusive date bounds for the existing billing API. */
@Component({
    selector: 'dmp-invoice-period-picker',
    standalone: false,
    providers: [
        {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => InvoicePeriodPickerComponent), multi: true},
        {provide: NG_VALIDATORS, useExisting: forwardRef(() => InvoicePeriodPickerComponent), multi: true}
    ],
    template: `
        <div class="calendar-period">
            <select [id]="inputId" [attr.aria-label]="label" [disabled]="disabled" [ngModel]="period" [ngModelOptions]="{standalone: true}"
                (ngModelChange)="choosePeriod($event === '' ? '' : $event.toString())" (blur)="onTouched()">
                <option [ngValue]="''">{{ (optional ? 'callInvoicePeriods.lastClosed' : 'callInvoicePeriods.choose') | translate }}</option>
                <option *ngFor="let item of choices" [ngValue]="item.value">{{ item.label }}</option>
            </select>
            <input *ngIf="period !== ''" type="number" min="1900" max="9999" [value]="year"
                [attr.aria-label]="'callInvoicePeriods.year' | translate:{label: label}" [disabled]="disabled"
                (change)="chooseYear($any($event.target).value)" (blur)="onTouched()">
        </div>`,
    styles: [`
        .calendar-period {display:flex;gap:.5rem;min-width:0}
        select,input {font:inherit;color:inherit;border:1px solid #cbd5e1;border-radius:6px;padding:.65rem;min-height:42px;background:#fff}
        select {min-width:0;flex:1;width:100%} input {width:6.5rem;flex:none}
        select:focus-visible,input:focus-visible {outline:2px solid #db2777;outline-offset:2px}
    `]
})
export class InvoicePeriodPickerComponent implements ControlValueAccessor, Validator {
    constructor(private readonly translate: TranslateService) {}

    @Input() frequency = 'M';
    @Input() boundary: 'start' | 'end' = 'start';
    @Input() optional = false;
    @Input() inputId = '';
    @Input() label = this.translate.instant('invoicing.common.period');
    value: Date | null = null;
    disabled = false;
    year = new Date().getFullYear();
    private invalidYear = false;
    private validatorChanged: () => void = () => {};
    onChange: (value: Date | null) => void = () => {};
    onTouched: () => void = () => {};

    get choices(): {value: number; label: string}[] {
        return this.frequency === 'T'
            ? [0, 1, 2, 3].map(value => ({value, label: this.translate.instant('callInvoicePeriods.quarter', {quarter: value + 1})}))
            : Array.from({length: 12}, (_, value) => ({value,
                label: new Intl.DateTimeFormat(this.translate.getCurrentLang() === 'en' ? 'en-GB' : 'fr-FR', {month: 'long'}).format(new Date(2020, value, 1))}));
    }

    get period(): number | '' {
        if (!this.value) { return ''; }
        return this.frequency === 'T' ? Math.floor(this.value.getMonth() / 3) : this.value.getMonth();
    }

    writeValue(value: Date | string | null): void {
        this.invalidYear = false;
        const date = value ? new Date(value) : null;
        this.value = date && !Number.isNaN(date.getTime()) ? date : null;
        if (this.value) { this.year = this.value.getFullYear(); }
    }
    registerOnChange(fn: (value: Date | null) => void): void { this.onChange = fn; }
    registerOnTouched(fn: () => void): void { this.onTouched = fn; }
    setDisabledState(value: boolean): void { this.disabled = value; }
    registerOnValidatorChange(fn: () => void): void { this.validatorChanged = fn; }
    validate(): ValidationErrors | null { return this.invalidYear ? {calendarYear: true} : null; }

    choosePeriod(value: string): void {
        if (value === '') { this.invalidYear = false; this.value = null; this.onChange(null); this.validatorChanged(); this.onTouched(); return; }
        this.emitPeriod(Number(value));
    }
    chooseYear(value: string): void {
        const year = Number(value);
        if (!Number.isInteger(year) || year < 1900 || year > 9999) {
            this.invalidYear = true;
            this.validatorChanged();
            this.onTouched();
            return;
        }
        this.invalidYear = false;
        const period = this.period;
        this.year = year;
        if (period !== '') { this.emitPeriod(period); }
        this.validatorChanged();
    }
    private emitPeriod(period: number): void {
        const month = this.frequency === 'T' ? period * 3 : period;
        const months = this.frequency === 'T' ? 3 : 1;
        this.value = this.boundary === 'start' ? new Date(this.year, month, 1) : new Date(this.year, month + months, 0);
        this.onChange(this.value);
        this.onTouched();
    }
}
